import { USP } from '../../cmp'
import createScriptTag from '../../helpers/DOM/createScriptTag'
import insertScript from '../../helpers/DOM/insertScript'
import type WebModel from '../../models/WebModel'
import type { IFLoCId } from '../../prebidFacade/s2sAdapter/helpers/eids'

export namespace PnG {
  const src = `https://pghub.io/js/pandg-sdk.js`
  const delay = 3000 // ms

  /** Public */
  export const init = (dependancies: PnGDependancies): void => {
    const callback = () => setTimeout(() => onTapadLoad(dependancies), delay)
    const script = createScriptTag(src)
    script.onload = callback
    insertScript(script)
  }

  /** Private */
  export const onTapadLoad = async ({ model, gdpr }: PnGDependancies) => {
    const config = getPnGConfig()
    const tcInfo = await checkAndGetTCString({ gdpr })
    if (!tcInfo.hasConsent) {
      return
    }
    const metadata = getPnGMetaData(tcInfo.gdpr_consent)
    const data = getPnGData({ model })

    sync({ metadata, config, data })
  }

  export const sync = (
    { metadata, config, data }: PnGSync,
    global = window
  ) => {
    const tagger = global.Tapad?.init(metadata, config)
    tagger && tagger.sync(data)
  }

  /** 
   * verifies that consent is accepted for P&G
   * and returns tcString
   */
  export const checkAndGetTCString = async (
    { gdpr }: Pick<PnGDependancies, 'gdpr'>,
  ): Promise<{ gdpr_consent: string | null, hasConsent: boolean }> => {
    if (gdpr.enableGdpr) {
      const { TCF } = await import(
        /* webpackChunkName: "gdpr"*/ '../../cmp/tcf/TCF'
      )

      // PnG's partner idfrom iab:  https://iabeurope.eu/vendor-list-tcf-v2-0/
      const PnGGvlId = 577
      const legitInterestConsent = TCF.tcModel.vendorLegitimateInterests.has(PnGGvlId)
      const vendorConsent = TCF.tcModel.vendorConsents.has(PnGGvlId)

      return {
        gdpr_consent: TCF.tcString,
        hasConsent: legitInterestConsent && vendorConsent
      }
    }

    return {
      gdpr_consent: null,
      hasConsent: true
    }
  }

  export const getPnGMetaData = (
    gdpr_consent: string | null,
    usp: Pick<typeof USP, 'uspString'> = USP
  ): PnGMetadata => {
    return {
      gdpr: gdpr_consent ? 1 : 0,
      gdpr_consent,
      bp_id: 'mediavine',
      ccpa: usp?.uspString || null
    }
  }

  export const getPnGConfig = (): PnGConfig => ({
    name: 'P&G',
    pixelUrl: 'https://pandg.tapad.com/tag'
  })

  export const getPnGData = (
    { model }: Pick<PnGDependancies, 'model'>,
    global: Pick<Window, 'pbjs' | '$mediavine'> = window
  ): PnGData => {
    const uids = global.pbjs.getUserIds()
    const uid2 = global.$mediavine.uid2 || uids.uid2?.id
    const id5 = global.$mediavine.id5Id || uids.id5id?.uid
    const flocId: IFLoCId = uids['flocId'] || {}
    const liveramp_idl = uids['idl_env']
    return {
      category: model.topLevelCategory.title,
      subcategory: model.category.title,
      uid2,
      id5,
      liveramp_idl,
      floc_value: flocId.id,
      floc_version: flocId.version
    }
  }
}

interface PnGMetadata {
  gdpr: 1 | 0 | null
  gdpr_consent: string | null
  ccpa: string | null
  bp_id: string
}

interface PnGConfig {
  name: 'P&G'
  pixelUrl: 'https://pandg.tapad.com/tag'
}

interface PnGData {
  category?: string
  subcategory?: string
  gender?: string
  age_range?: string
  floc_value?: string
  floc_version?: string
  uid2?: string
  id5?: string
  liveamp_idl?: string
  [key: string]: any
}

interface PnGSync {
  metadata: PnGMetadata
  config: PnGConfig
  data: PnGData
}

export interface PnGDependancies {
  model: {
    topLevelCategory: Pick<WebModel['topLevelCategory'], 'title'>
    category: Pick<WebModel['category'], 'title'>
  }
  gdpr: Pick<IGDPR, 'enableGdpr'>
}

declare global {
  interface Window {
    Tapad: {
      init: (
        metadata: PnGMetadata,
        config: PnGConfig
      ) => { sync: (data: PnGData) => void }
    }
  }
}
